import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { getPlatformIcon, getPlatformColor } from '../platformIcons';
import './Services.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

export default function Services() {
  const [services, setServices] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePlatform, setActivePlatform] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/settings/metadata`)
      .then((r) => r.json())
      .then((d) => setPlatforms(Array.isArray(d.platforms) ? d.platforms : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (activePlatform) params.set('platform', activePlatform);
    if (activeCategory) params.set('category', activeCategory);
    if (search) params.set('search', search);
    const timer = setTimeout(() => {
      fetch(`${API}/services?${params}`)
        .then((r) => r.json())
        .then((data) => setServices(Array.isArray(data) ? data : []))
        .catch(() => setServices([]))
        .finally(() => setLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [activePlatform, activeCategory, search]);

  useEffect(() => {
    if (platforms.length === 0 && !activePlatform) {
      const derived = [...new Set(services.map((s) => s.platform))].sort();
      if (derived.length > 0) setPlatforms(derived);
    }
  }, [services]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const allCategories = [...new Set(services.map((s) => s.category))];

  const filtered = services.filter((s) => {
    const matchCategory = activeCategory ? s.category === activeCategory : true;
    const matchSearch = search
      ? s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.platform.toLowerCase().includes(search.toLowerCase())
      : true;
    return matchCategory && matchSearch;
  });

  return (
    <div className="services-page">
      <div className="section-label">Platforms</div>
      <div className="platform-filters">
        <button
          className={`platform-chip ${activePlatform === '' ? 'active' : ''}`}
          onClick={() => setActivePlatform('')}
        >
          All
        </button>
        {platforms.map((name) => (
          <button
            key={name}
            className={`platform-chip ${activePlatform === name ? 'active' : ''}`}
            onClick={() => setActivePlatform(activePlatform === name ? '' : name)}
            style={activePlatform === name ? { borderColor: getPlatformColor(name), background: `${getPlatformColor(name)}20` } : {}}
          >
            <FontAwesomeIcon icon={getPlatformIcon(name)} style={{ color: getPlatformColor(name) }} />
            <span>{name}</span>
          </button>
        ))}
      </div>

      <div className="search-bar services-search">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          className="search-input"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="section-label">Categories</div>
      <div className="category-dropdown" ref={dropdownRef} onClick={() => setDropdownOpen(!dropdownOpen)}>
        <span className="dropdown-selected">
          {activeCategory || 'All Categories'}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
        {dropdownOpen && (
          <div className="dropdown-menu">
            <div
              className={`dropdown-item ${activeCategory === '' ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setActiveCategory(''); setDropdownOpen(false); }}
            >
              All Categories
            </div>
            {allCategories.map((cat) => (
              <div
                key={cat}
                className={`dropdown-item ${activeCategory === cat ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setActiveCategory(cat); setDropdownOpen(false); }}
              >
                {cat}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="services-table-wrapper">
        <table className="services-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Service</th>
              <th>Platform</th>
              <th>Category</th>
              <th>Rate</th>
              <th>Min</th>
              <th>Max</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="services-loading">Loading services...</td>
              </tr>
            )}
            {!loading && filtered.map((s) => (
              <tr key={s.id}>
                <td className="service-id">{s.id}</td>
                <td className="service-name-cell">{s.name}</td>
                <td>
                  <span className="platform-badge">
                    <FontAwesomeIcon icon={getPlatformIcon(s.platform)} style={{ marginRight: 6, color: getPlatformColor(s.platform) }} />
                    {s.platform}
                  </span>
                </td>
                <td>
                  <span className="category-badge">{s.category}</span>
                </td>
                <td className="service-rate-cell">TSHS {Number(s.rate).toLocaleString()} / 1000</td>
                <td>{Number(s.min_quantity).toLocaleString()}</td>
                <td>{Number(s.max_quantity).toLocaleString()}</td>
                <td className="service-desc">{s.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
